import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';
import { track } from '@/lib/analytics';
import {
  isGoal, onboardingEnabled, openIntentChooser, setOnboardingActive, startIntentGuide, type Intent,
} from '@/lib/onboarding';

/** Open the tutorial video from anywhere (the header button, the account menu). */
export const TUTORIAL_VIDEO_EVENT = 'copyfi:tutorial-video';
/*
 * With the welcome by intent on (lib/onboarding.ts), the same two buttons open
 * the chooser instead: it is how anyone watches a video again and starts a
 * guide again, and how a choice is made again.
 */
export const openTutorialVideo = () => {
  if (onboardingEnabled()) openIntentChooser();
  else window.dispatchEvent(new Event(TUTORIAL_VIDEO_EVENT));
};
/** The video for an answer of the chooser; a guide or Quick Learn follows it. */
export const playWelcomeVideo = (intent: Intent) =>
  window.dispatchEvent(new CustomEvent(TUTORIAL_VIDEO_EVENT, { detail: { intent } }));

/** Quick Learn's first run, after the general video (QuickLearnTour.tsx). */
export const QUICK_LEARN_FIRST_EVENT = 'copyfi:quicklearn:first';

const TUTORIAL_VIDEO_CLOSED_EVENT = 'copyfi:tutorial-video:closed';
const TUTORIAL_SRC = '/videos/copyfi-tutorial.mp4';
const SEEN_PREFIX = 'copyfi_tutorial_video_seen:';
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;
/** Off: Get Started is a new account's first screen (2026-10-21). */
const AUTO_INTRO = false;

/*
 * The three welcome videos (2026-10-21), 17 to 19.5 MB each, served as they
 * were encoded (H.264, faststart, subtitles burnt in). Served by the app from
 * public/videos, like the tutorial: publishing them on copyfi.ai would mean
 * redeploying the site for three files (2026-10-22).
 */
const WELCOME_BASE = '/videos/';
const WELCOME: Record<'copy_trading' | 'strategy_builder' | 'ai_trading', string> = {
  copy_trading: 'welcome-copy-trading',
  strategy_builder: 'welcome-strategy-builder',
  ai_trading: 'welcome-ai-trading',
};
const CHOOSER_KEY: Record<'copy_trading' | 'strategy_builder' | 'ai_trading', string> = {
  copy_trading: 'copy',
  strategy_builder: 'builder',
  ai_trading: 'ai',
};

/*
 * Whether the automatic first showing is on screen right now. The Quick Learn
 * tour also starts by itself on a first visit; two overlays fighting for the
 * same first screen would leave a new user with neither. So the video goes
 * first and the tour waits for it (afterTutorialVideo, QuickLearnTour.tsx).
 */
let autoShowing = false;

/** Run `cb` once the automatic first showing is closed, or now if there is none. */
export function afterTutorialVideo(cb: () => void): () => void {
  if (!autoShowing) { cb(); return () => {}; }
  window.addEventListener(TUTORIAL_VIDEO_CLOSED_EVENT, cb, { once: true });
  return () => window.removeEventListener(TUTORIAL_VIDEO_CLOSED_EVENT, cb);
}

/****************
 * THE TUTORIAL VIDEO. Mishka, 2026-10-03: when someone creates an account the
 * walkthrough video pops up for them to watch and understand the platform,
 * with a button to watch it again at any time.
 *
 * Automatic only for accounts created in the last day, so everyone who signed
 * up before it existed is not interrupted by it. Once per person: the flag is
 * on their Clerk account (follows them to another device) and in this browser
 * (in case the Clerk write fails). Never for an admin viewing someone else.
 *
 * 2026-10-21: it also plays the video of an answer of the welcome chooser,
 * with Skip always there. At the end, or on Skip, the guide of that answer
 * starts; after the general video ("I don't know", "I want everything"),
 * Quick Learn, as on a first visit before.
 ****************/
export function TutorialVideoDialog() {
  const { t } = useTranslation('common');
  const { t: tl } = useTranslation('learn');
  const { user, isLoaded } = useUser();
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState<Intent | null>(null);
  const autoCheckedRef = useRef(false);
  const milestonesRef = useRef(new Set<number>());
  const finishedRef = useRef(false);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const chosen = (e as CustomEvent<{ intent?: Intent }>).detail?.intent ?? null;
      setIntent(chosen);
      milestonesRef.current = new Set();
      finishedRef.current = false;
      setOpen(true);
    };
    window.addEventListener(TUTORIAL_VIDEO_EVENT, onOpen);
    return () => window.removeEventListener(TUTORIAL_VIDEO_EVENT, onOpen);
  }, []);

  useEffect(() => {
    /* No automatic showing since 2026-10-21 (engagement redesign): a new
       account starts on Get Started, which gets them to one result instead of
       explaining everything at once. The video stays one click away in the
       header (openTutorialVideo). */
    if (!AUTO_INTRO) return;
    // The chooser takes this first showing over when the welcome by intent is on.
    if (autoCheckedRef.current || !isLoaded || !user || isViewingAsSomeoneElse() || onboardingEnabled()) return;
    autoCheckedRef.current = true;

    const key = SEEN_PREFIX + user.id;
    let seenHere = false;
    try { seenHere = localStorage.getItem(key) === '1'; } catch { /* private mode */ }
    const seen = seenHere || user.unsafeMetadata?.tutorialVideoSeen === true;
    const created = user.createdAt ? new Date(user.createdAt).getTime() : 0;
    if (seen || Date.now() - created > NEW_ACCOUNT_MS) return;

    autoShowing = true;
    setOpen(true);
    try { localStorage.setItem(key, '1'); } catch { /* private mode */ }
    user.update({ unsafeMetadata: { ...user.unsafeMetadata, tutorialVideoSeen: true } }).catch(() => {});
  }, [isLoaded, user]);

  useEffect(() => {
    setOnboardingActive('video', open && intent !== null);
    return () => setOnboardingActive('video', false);
  }, [open, intent]);

  /* After an answer of the chooser: the guide, or Quick Learn. Once. */
  const proceed = (skipped: boolean, at: number) => {
    if (!intent || finishedRef.current) return;
    finishedRef.current = true;
    if (skipped) track('onboarding_video', intent, undefined, { skipped: true, at_s: Math.round(at) });
    const chosen = intent;
    window.setTimeout(() => {
      if (isGoal(chosen)) startIntentGuide(chosen);
      else window.dispatchEvent(new Event(QUICK_LEARN_FIRST_EVENT));
    }, 250);
  };

  const onOpenChange = (next: boolean, at = 0) => {
    setOpen(next);
    if (!next) proceed(true, at);
    if (!next && autoShowing) {
      autoShowing = false;
      window.dispatchEvent(new Event(TUTORIAL_VIDEO_CLOSED_EVENT));
    }
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const onTime = () => {
    const v = videoRef.current;
    if (!intent || !v || !v.duration) return;
    const pct = Math.floor((v.currentTime / v.duration) * 4) * 25;
    for (const p of [25, 50, 75]) {
      if (pct >= p && !milestonesRef.current.has(p)) {
        milestonesRef.current.add(p);
        track('onboarding_video', intent, undefined, { watched_pct: p, skipped: false });
      }
    }
  };
  // Ended on its own: on to the guide without a press.
  const onEnded = () => {
    if (!intent) return;
    if (!milestonesRef.current.has(100)) {
      milestonesRef.current.add(100);
      track('onboarding_video', intent, undefined, { watched_pct: 100, skipped: false });
    }
    proceed(false, 0);
    setOpen(false);
  };

  const goal = intent && isGoal(intent) ? intent : null;
  const src = goal ? `${WELCOME_BASE}copyfi-${WELCOME[goal]}.mp4` : TUTORIAL_SRC;
  const poster = goal ? `${WELCOME_BASE}copyfi-${WELCOME[goal]}-poster.jpg` : undefined;
  const title = goal ? tl(`guide.chooser.${CHOOSER_KEY[goal]}.title`) : t('tutorialVideo.title');
  const description = goal ? tl(`guide.chooser.${CHOOSER_KEY[goal]}.body`) : t('tutorialVideo.description');

  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next, videoRef.current?.currentTime ?? 0)}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 text-start">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {open && (
          <video
            ref={videoRef}
            key={src}
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
            preload="metadata"
            onTimeUpdate={onTime}
            onEnded={onEnded}
            className="w-full aspect-video bg-black"
          />
        )}
        <div className="flex justify-end px-6 py-4">
          {intent ? (
            <Button variant="outline" onClick={() => onOpenChange(false, videoRef.current?.currentTime ?? 0)}>
              {tl('guide.video.skip')}
            </Button>
          ) : (
            <Button onClick={() => onOpenChange(false)}>{t('tutorialVideo.close')}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
